function parseHex(hex) {
  if (!hex) return null;
  let normalized = String(hex).trim().replace("#", "");
  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map((char) => char + char)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function normalizeColor(color, fallback) {
  if (!color || typeof color !== "string") return fallback;
  const trimmed = color.trim();
  if (trimmed.startsWith("#")) {
    if (trimmed.length === 9) {
      return trimmed.slice(0, 7);
    }
    return trimmed;
  }
  if (trimmed.startsWith("rgb") || trimmed.startsWith("hsl")) {
    return trimmed;
  }
  if (/^[0-9a-fA-F]{3}$/.test(trimmed) || /^[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `#${trimmed}`;
  }
  if (/^[0-9a-fA-F]{8}$/.test(trimmed)) {
    return `#${trimmed.slice(0, 6)}`;
  }
  return trimmed;
}

export const TEXT_ON_LIGHT = "#181c20";
export const TEXT_ON_DARK = "#ffffff";

export function getContrastTextColor(
  backgroundColor,
  { dark = TEXT_ON_LIGHT, light = TEXT_ON_DARK } = {}
) {
  const rgb = parseHex(normalizeColor(backgroundColor, "#0275ff"));
  if (!rgb) return light;
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness > 155 ? dark : light;
}

export function getBrandColor(bookingGeneral = {}) {
  const settings = Array.isArray(bookingGeneral)
    ? bookingGeneral[0] || {}
    : bookingGeneral || {};
  const raw =
    settings.color ?? settings.Color ?? settings.themeColor ?? settings.backgroundColor;
  return normalizeColor(raw, "#00509e");
}

export function getButtonColor(bookingGeneral = {}) {
  const settings = Array.isArray(bookingGeneral)
    ? bookingGeneral[0] || {}
    : bookingGeneral || {};
  return normalizeColor(settings.buttoncolor, "#0275FF");
}

export function getFontColor(bookingGeneral = {}) {
  const settings = Array.isArray(bookingGeneral)
    ? bookingGeneral[0] || {}
    : bookingGeneral || {};
  const raw =
    settings.fontColor ?? settings.fontcolor ?? settings.FontColor;
  return normalizeColor(raw, getButtonColor(settings));
}

export function mixWithWhite(hex, whiteRatio = 0.88) {
  const color = parseHex(hex);
  if (!color) return "rgb(239, 246, 255)";
  const r = color.r + (255 - color.r) * whiteRatio;
  const g = color.g + (255 - color.g) * whiteRatio;
  const b = color.b + (255 - color.b) * whiteRatio;
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

export function applyBookingTheme(bookingGeneral = {}) {
  const settings = Array.isArray(bookingGeneral)
    ? bookingGeneral[0] || {}
    : bookingGeneral || {};
  const buttonColor = getButtonColor(settings);
  const brandColor = getBrandColor(settings);
  const buttonTextColor = getContrastTextColor(buttonColor);
  const brandTextColor = getContrastTextColor(brandColor);
  const root = document.documentElement;

  root.style.setProperty("--booking-button-color", buttonColor);
  root.style.setProperty("--booking-button-font-color", buttonTextColor);
  root.style.setProperty("--booking-on-brand-color", brandTextColor);
  root.style.setProperty("--booking-brand-color", brandColor);
  root.style.setProperty("--booking-header-color", brandColor);
  root.style.setProperty("--booking-sidebar-color", brandColor);
  root.style.setProperty("--booking-button-color-light", mixWithWhite(buttonColor, 0.9));
  root.style.setProperty("--booking-button-color-border", mixWithWhite(buttonColor, 0.72));
  root.style.setProperty("--booking-button-color-hover", mixWithWhite(buttonColor, 0.82));
  root.style.setProperty("--calendar-event-accent", buttonColor);
  root.style.setProperty("--calendar-event-bg", mixWithWhite(buttonColor, 0.91));
  root.style.setProperty("--calendar-event-bg-hover", mixWithWhite(buttonColor, 0.86));
  root.style.setProperty("--calendar-event-border", mixWithWhite(buttonColor, 0.76));

  const rgb = parseHex(buttonColor);
  if (rgb) {
    const shadowR = Math.min(255, rgb.r + 80);
    const shadowG = Math.min(255, rgb.g + 80);
    const shadowB = Math.min(255, rgb.b + 80);
    const hoverR = Math.min(255, rgb.r + 60);
    const hoverG = Math.min(255, rgb.g + 60);
    const hoverB = Math.min(255, rgb.b + 60);
    const bgR = Math.min(255, rgb.r + 120);
    const bgG = Math.min(255, rgb.g + 120);
    const bgB = Math.min(255, rgb.b + 120);

    root.style.setProperty(
      "--dynamic-event-bg",
      `rgb(${shadowR}, ${shadowG}, ${shadowB})`
    );
    root.style.setProperty(
      "--dynamic-event-bg-hover",
      `rgb(${hoverR}, ${hoverG}, ${hoverB})`
    );
    root.style.setProperty(
      "--dynamic-calendar-bg",
      `rgb(${bgR}, ${bgG}, ${bgB})`
    );
  }
}
